// usePOSVendaDireta - Checkout handlers and side-effects for POSVendaDireta
import { useState } from "react";
import { toast } from "sonner";
import { printReceipt } from "@/utils/printService";

export function usePOSVendaDireta({
  cart,
  subtotal,
  total,
  discountAmount,
  discountType,
  discountValue,
  discountReason,
  orderNotes,
  setOrderNotes,
  clearCart,
  isEmpty,
  requireCustomer,
  customerName,
  customerPhone,
  setCustomerName,
  setCustomerPhone,
  orderSource,
  tableNumber,
  deliveryCustomerName,
  deliveryCustomerPhone,
  deliveryAddress,
  deliveryNotes,
  deliveryPaymentMethod,
  setDeliveryAddress,
  setDeliveryNotes,
  posConfig,
  restaurantData,
  waConnected,
  authAxios,
  onBack,
}) {
  const [orderComplete, setOrderComplete] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [showCashDialog, setShowCashDialog] = useState(false);
  const [showDelivererModal, setShowDelivererModal] = useState(false);
  const [deliverers, setDeliverers] = useState([]);
  const [createdOrderId, setCreatedOrderId] = useState(null);
  const [assigningDeliverer, setAssigningDeliverer] = useState(null);

  // Normalize POS payment method aliases to canonical backend names
  const _normalizeMethod = (m) => {
    if (m === "tarjeta_na_entrega" || m === "card_delivery") return "card_restaurant";
    return m;
  };

  const buildItems = () =>
    cart.map(item => ({
      product_id: item.product_id,
      product_name: item.product_name,
      quantity: item.quantity,
      unit_price: item.unit_price,
      notes: item.notes || "",
      modifiers: item.modifiers || null,
      selected_variations: item.selected_variations?.length > 0 ? item.selected_variations : null,
    }));

  const buildDiscountFields = () => ({
    discount_amount: discountAmount || 0,
    discount_type: discountType || "fixed",
    discount_value: discountValue || 0,
    discount_reason: discountReason || "",
  });

  const handleCheckout = async (rawMethod) => {
    const method = _normalizeMethod(rawMethod);
    if (isEmpty) return;
    if (requireCustomer && !customerName.trim()) { toast.error("Nome do cliente é obrigatório"); return; }
    if (method === "cash") { setShowCashDialog(true); return; }

    const totalSnapshot = total;
    const subtotalSnapshot = subtotal;

    setProcessing(true);
    try {
      const orderData = {
        items: buildItems(),
        payment_method: method,
        table_number: tableNumber,
        ...buildDiscountFields(),
        total,
        notes: orderNotes.trim() || undefined,
        ...(requireCustomer && {
          customer_name: customerName.trim(),
          customer_phone: customerPhone.trim() || undefined,
          source: orderSource || "pos",
          order_type: "pickup",
        }),
      };
      console.log("[POS_SUBMIT]", { itemCount: cart.length, subtotal, total, discountAmount, method });
      const endpoint = (requireCustomer && customerPhone.trim()) ? "/orders" : "/pos/quick-order";
      const res = await authAxios.post(endpoint, orderData);
      const apiTotal = res.data?.total;
      console.log("[POS_MODAL_TOTAL]", { api_total: apiTotal, snapshot_total: totalSnapshot, display_total: apiTotal ?? totalSnapshot });
      console.log("[ORDER_CREATE]", { id: res.data.id || res.data.order_id, resTotal: apiTotal, uiTotal: totalSnapshot });

      setOrderComplete({
        id: res.data.order_id || res.data.id,
        items: cart,
        subtotal: subtotalSnapshot,
        ...buildDiscountFields(),
        total: apiTotal ?? totalSnapshot,
        payment_method: method,
      });
      clearCart();
      setOrderNotes("");
      toast.success("Pedido criado com sucesso!");
      if (!waConnected) toast.warning("WhatsApp desconectado — o cliente não receberá confirmação automática.", { duration: 8000 });

      if (posConfig?.auto_print) {
        try {
          await printReceipt({ orderId: res.data.order_id, items: cart, subtotal: subtotalSnapshot, ...buildDiscountFields(), total: totalSnapshot }, {}, restaurantData);
        } catch (e) { console.error("Print error:", e); }
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao criar pedido"); }
    setProcessing(false);
  };

  const handleCashCheckout = async (received, change) => {
    setShowCashDialog(false);
    const totalSnapshot = total;
    const subtotalSnapshot = subtotal;
    setProcessing(true);
    try {
      const orderData = {
        items: buildItems(),
        payment_method: "cash",
        cash_received: received,
        cash_change: change,
        table_number: tableNumber,
        ...buildDiscountFields(),
        notes: orderNotes.trim() || undefined,
        total,
        ...(requireCustomer && {
          customer_name: customerName.trim(),
          customer_phone: customerPhone.trim() || undefined,
          source: orderSource || "pos",
          order_type: "pickup",
        }),
      };
      const endpoint = (requireCustomer && customerPhone.trim()) ? "/orders" : "/pos/quick-order";
      const res = await authAxios.post(endpoint, orderData);
      const apiTotal = res.data?.total;
      console.log("[POS_MODAL_TOTAL]", { api_total: apiTotal, snapshot_total: totalSnapshot, display_total: apiTotal ?? totalSnapshot });

      setOrderComplete({
        id: res.data.order_id || res.data.id,
        items: cart,
        subtotal: subtotalSnapshot,
        ...buildDiscountFields(),
        total: apiTotal ?? totalSnapshot,
        payment_method: "cash",
        cash_received: received,
        cash_change: change,
      });
      clearCart();
      setOrderNotes("");
      toast.success(`Pedido criado! Troco: ${change.toFixed(2)}€`);
      if (!waConnected) toast.warning("WhatsApp desconectado — o cliente não receberá confirmação automática.", { duration: 8000 });
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao criar pedido"); }
    setProcessing(false);
  };

  const handleTableOrder = async () => {
    if (isEmpty) return;
    setProcessing(true);
    try {
      const orderData = {
        items: buildItems(),
        table_number: tableNumber,
        payment_method: "pending",
        total: subtotal,
        notes: orderNotes.trim() || undefined,
      };
      await authAxios.post("/pos/quick-order", orderData);
      clearCart();
      setOrderNotes("");
      toast.success("Pedido enviado para a cozinha!");
      onBack?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao enviar pedido"); }
    setProcessing(false);
  };

  const handleDeliveryCheckout = async () => {
    if (!deliveryCustomerName.trim()) { toast.error("Nome do cliente obrigatório"); return; }
    if (!deliveryAddress.trim()) { toast.error("Morada de entrega obrigatória"); return; }
    if (isEmpty) { toast.error("Carrinho vazio"); return; }
    setProcessing(true);
    try {
      const res = await authAxios.post("/orders", {
        source: deliveryCustomerPhone.trim() ? "whatsapp" : "phone",
        customer_name: deliveryCustomerName.trim(),
        customer_phone: deliveryCustomerPhone.trim() || undefined,
        delivery_address: deliveryAddress.trim(),
        notes: [orderNotes.trim(), deliveryNotes.trim() ? `Entrega: ${deliveryNotes.trim()}` : ""].filter(Boolean).join(" | ") || undefined,
        payment_method: deliveryPaymentMethod,
        ...buildDiscountFields(),
        total,
        items: buildItems(),
      });
      const orderId = res.data?.id || res.data?.order_id;
      setCreatedOrderId(orderId);
      clearCart();
      setOrderNotes("");
      setDeliveryAddress("");
      setDeliveryNotes("");
      toast.success("Pedido delivery criado!");
      try {
        const dr = await authAxios.get("/deliverers/");
        setDeliverers((dr.data || []).filter(d => d.active !== false));
      } catch { setDeliverers([]); }
      setShowDelivererModal(true);
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao criar pedido delivery"); }
    setProcessing(false);
  };

  const handleAssignDeliverer = async (delivererId) => {
    if (!createdOrderId) return;
    setAssigningDeliverer(delivererId);
    try {
      await authAxios.patch(`/orders/${createdOrderId}/assign-deliverer?deliverer_id=${encodeURIComponent(delivererId)}`);
      toast.success("Estafeta atribuído!");
      setShowDelivererModal(false);
      setCreatedOrderId(null);
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao atribuir estafeta"); }
    setAssigningDeliverer(null);
  };

  const handlePrint = async () => {
    if (!orderComplete) return;
    try {
      await printReceipt(
        { ...orderComplete, orderId: orderComplete.id, change_amount: orderComplete.cash_change },
        {},
        restaurantData
      );
      toast.success("Recibo impresso!");
    } catch { toast.error("Erro ao imprimir"); }
  };

  const handleNewOrder = () => {
    setOrderComplete(null);
    clearCart();
    setCustomerName("");
    setCustomerPhone("");
  };

  const closeDelivererModal = () => { setShowDelivererModal(false); setCreatedOrderId(null); };

  return {
    orderComplete,
    processing,
    showCashDialog,
    setShowCashDialog,
    showDelivererModal,
    deliverers,
    assigningDeliverer,
    handleCheckout,
    handleCashCheckout,
    handleTableOrder,
    handleDeliveryCheckout,
    handleAssignDeliverer,
    handlePrint,
    handleNewOrder,
    closeDelivererModal,
  };
}
